/**
 * The leaderboard API (server/main.py). One POST per finished round, one GET per board.
 * Offline-friendly: a score that can't be sent waits in localStorage and goes next time.
 */
const API = (import.meta.env.VITE_API as string | undefined) ?? 'https://coco.lunaloo.net/api';
const PENDING = 'coco.pending';
export const GAME = 'cocoshot';

export interface Entry {
  player: string;
  name: string;
  score: number;
  mistakes: number;
  detail: { hits?: number; farthest?: number; shots?: number } | null;
  at: number;
}

export interface Board {
  game: string;
  entries: Entry[];
  /** this player's standing, if they have posted */
  me: { best: number; rank: number; players: number } | null;
}

export interface ScorePost {
  game: string;
  player: string;
  name: string;
  score: number;
  mistakes: number;
  detail?: Record<string, unknown>;
}

async function post(body: ScorePost): Promise<Board> {
  const r = await fetch(`${API}/v1/scores`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error(`${r.status}`);
  return (await r.json()) as Board;
}

function pending(): ScorePost[] {
  try {
    return (JSON.parse(localStorage.getItem(PENDING) ?? '[]') as ScorePost[]) ?? [];
  } catch {
    return [];
  }
}

function setPending(list: ScorePost[]): void {
  try {
    localStorage.setItem(PENDING, JSON.stringify(list.slice(-50)));
  } catch {
    /* no storage */
  }
}

/** post a round; null when offline (it is queued) or rejected; 'reserved' when the name belongs to someone with a PIN */
export async function submitScore(body: ScorePost): Promise<Board | null | 'reserved'> {
  try {
    const board = await post(body);
    void flushPending();
    return board;
  } catch (e) {
    if (e instanceof Error && e.message === '409') return 'reserved';
    // a 4xx means the server rejected it (bad score); only network failures are queued
    if (e instanceof Error && /^4\d\d$/.test(e.message)) return null;
    setPending([...pending(), body]);
    return null;
  }
}

export interface Claim {
  player: string;
  name: string;
  /** true when the name was just reserved, false when it was a sign-in */
  claimed: boolean;
  /** what the account has stored (earned items) */
  inventory?: Partial<Record<'rock' | 'pineapple' | 'fish' | 'shoe' | 'super', number>>;
}

/** store the account's earned items; fire and forget */
export async function putInventory(player: string, inventory: Record<string, number>): Promise<boolean> {
  try {
    const r = await fetch(`${API}/v1/players/${encodeURIComponent(player)}/inventory`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ inventory }) });
    return r.ok;
  } catch {
    return false;
  }
}

/** reserve a name with a PIN, or sign in to it. 'wrong' for a bad PIN, 'bad' for a malformed one, null offline */
export async function claimName(name: string, pin: string, player: string): Promise<Claim | 'wrong' | 'bad' | null> {
  try {
    const r = await fetch(`${API}/v1/claim`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, pin, player }) });
    if (r.status === 403) return 'wrong';
    if (r.status === 400) return 'bad';
    if (!r.ok) return null;
    return (await r.json()) as Claim;
  } catch {
    return null;
  }
}

export interface Stats {
  id: string;
  name: string;
  reserved: boolean;
  since: number;
  rounds: number;
  hits: number;
  shots: number;
  farthest: number;
  distance: number;
  seconds: number;
  games: Record<string, { rounds: number; best: number; last: number; hits: number }>;
  tally: Record<string, number>;
  first: number | null;
  last: number | null;
}

export async function fetchStats(player: string): Promise<Stats | null> {
  try {
    const r = await fetch(`${API}/v1/players/${encodeURIComponent(player)}/stats`);
    if (!r.ok) return null;
    return (await r.json()) as Stats;
  } catch {
    return null;
  }
}

/** send anything that couldn't be sent earlier */
export async function flushPending(): Promise<void> {
  const list = pending();
  if (!list.length) return;
  const left: ScorePost[] = [];
  for (const s of list) {
    try {
      await post(s);
    } catch (e) {
      if (!(e instanceof Error && /^4\d\d$/.test(e.message))) left.push(s);
    }
  }
  setPending(left);
}

export async function fetchBoard(player: string, limit = 25, game: string = GAME): Promise<Board | null> {
  try {
    const r = await fetch(`${API}/v1/leaderboard/${encodeURIComponent(game)}?player=${encodeURIComponent(player)}&limit=${limit}`);
    if (!r.ok) return null;
    return (await r.json()) as Board;
  } catch {
    return null;
  }
}

/** tell the server the player's new name (their past scores show it too) */
export async function renamePlayer(player: string, name: string): Promise<boolean> {
  try {
    const r = await fetch(`${API}/v1/players/${encodeURIComponent(player)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
    return r.ok;
  } catch {
    return false;
  }
}
